import { supabase } from '@/lib/supabase';
import { WpAppliedChange, PageImpactSummary } from '@/types/api/seo';

function mapRow(row: any): WpAppliedChange {
  return {
    id: row.id,
    projectId: row.project_id,
    opportunityId: row.opportunity_id,
    wpPostId: row.wp_post_id,
    changeType: row.change_type,
    previousValue: row.previous_value,
    newValue: row.new_value,
    pageUrl: row.page_url,
    keywordId: row.keyword_id,
    positionBefore: row.position_before,
    ctrBefore: row.ctr_before,
    clicksBefore: row.clicks_before,
    impressionsBefore: row.impressions_before,
    positionAfter: row.position_after,
    ctrAfter: row.ctr_after,
    clicksAfter: row.clicks_after,
    impressionsAfter: row.impressions_after,
    afterMeasuredAt: row.after_measured_at,
    impactStatus: row.impact_status ?? 'pending',
    appliedBy: row.applied_by,
    appliedAt: row.applied_at,
  };
}

function computeImpactStatus(change: WpAppliedChange): WpAppliedChange['impactStatus'] {
  if (change.positionBefore == null || change.positionAfter == null) return 'insufficient_data';
  const delta = change.positionBefore - change.positionAfter;
  if (Math.abs(delta) < 0.5) return 'no_change';
  return delta > 0 ? 'improved' : 'declined';
}

const seoImpactService = {
  async getByPageUrl(projectId: string, pageUrl: string): Promise<WpAppliedChange[]> {
    const { data, error } = await supabase
      .from('seo_wp_applied_changes')
      .select('*')
      .eq('project_id', projectId)
      .eq('page_url', pageUrl)
      .order('applied_at', { ascending: false });

    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  async getByKeywordId(keywordId: string): Promise<WpAppliedChange[]> {
    const { data, error } = await supabase
      .from('seo_wp_applied_changes')
      .select('*')
      .eq('keyword_id', keywordId)
      .order('applied_at', { ascending: false });

    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  async getByProject(projectId: string): Promise<WpAppliedChange[]> {
    const { data, error } = await supabase
      .from('seo_wp_applied_changes')
      .select('*')
      .eq('project_id', projectId)
      .order('applied_at', { ascending: false });

    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  getImpactSummaryByPage(changes: WpAppliedChange[]): Map<string, PageImpactSummary> {
    const map = new Map<string, PageImpactSummary>();

    for (const change of changes) {
      const key = change.pageUrl ?? `wp-${change.wpPostId}`;
      const existing = map.get(key);

      if (!existing) {
        map.set(key, {
          totalChanges: 1,
          latestChangeAt: change.appliedAt,
          impactStatus: change.impactStatus ?? 'pending',
          positionDelta: change.positionBefore != null && change.positionAfter != null
            ? change.positionBefore - change.positionAfter
            : null,
          changes: [change],
        });
      } else {
        existing.totalChanges++;
        if (change.appliedAt > existing.latestChangeAt) {
          existing.latestChangeAt = change.appliedAt;
        }
        existing.changes.push(change);

        // Compute aggregate impact status
        const statuses = existing.changes.map((c) => c.impactStatus);
        const hasImproved = statuses.includes('improved');
        const hasDeclined = statuses.includes('declined');
        if (hasImproved && hasDeclined) existing.impactStatus = 'mixed';
        else if (hasImproved) existing.impactStatus = 'improved';
        else if (hasDeclined) existing.impactStatus = 'declined';
        else if (statuses.every((s) => s === 'no_change')) existing.impactStatus = 'no_change';
        else existing.impactStatus = 'pending';

        // Position delta: from earliest change's before to latest after
        const sorted = [...existing.changes].sort((a, b) => a.appliedAt.localeCompare(b.appliedAt));
        const firstBefore = sorted.find((c) => c.positionBefore != null)?.positionBefore;
        const lastAfter = [...sorted].reverse().find((c) => c.positionAfter != null)?.positionAfter;
        existing.positionDelta = firstBefore != null && lastAfter != null ? firstBefore - lastAfter : null;
      }
    }

    return map;
  },

  async updateAfterMetrics(changeId: string, metrics: {
    position: number | null;
    ctr: number | null;
    clicks: number | null;
    impressions: number | null;
  }): Promise<void> {
    // First get the change to compute impact status
    const { data: change } = await supabase
      .from('seo_wp_applied_changes')
      .select('position_before')
      .eq('id', changeId)
      .single();

    let impactStatus: string = 'insufficient_data';
    if (change?.position_before != null && metrics.position != null) {
      const delta = change.position_before - metrics.position;
      if (Math.abs(delta) < 0.5) impactStatus = 'no_change';
      else impactStatus = delta > 0 ? 'improved' : 'declined';
    }

    const { error } = await supabase
      .from('seo_wp_applied_changes')
      .update({
        position_after: metrics.position,
        ctr_after: metrics.ctr,
        clicks_after: metrics.clicks,
        impressions_after: metrics.impressions,
        after_measured_at: new Date().toISOString(),
        impact_status: impactStatus,
      })
      .eq('id', changeId);

    if (error) throw error;
  },
};

export default seoImpactService;
